import { GuildPermissions } from '@/services/GuildUserService/GuildUserService'
import { StreamingUrlArgs } from '@/types/codegen-federation'

/**
 * Builds StreamingUrlArgs based on user entitlements.
 *
 * - `stream_in_4k` entitlement (Premium/Employee): max 2160p (4K), default 1080p
 * - `stream_in_1080p` entitlement (Basic tiers): max 1080p, default 1080p
 * - No streaming entitlement (free users): segmentFormat only (Hydra defaults: max 1080p, default 480p)
 *
 * Hydra gates 4K server-side: non-entitled users requesting RESOLUTION_2160 get downgraded.
 */
export function getStreamingArgs(options?: {
  guildPermissions?: Pick<GuildPermissions, 'streamIn4k' | 'streamIn1080p'>
}): StreamingUrlArgs {
  if (options?.guildPermissions?.streamIn4k) {
    return PREMIUM_STREAMING_ARGS
  }

  if (options?.guildPermissions?.streamIn1080p) {
    return BASIC_STREAMING_ARGS
  }

  return DEFAULT_STREAMING_ARGS
}

/**
 * Builds StreamingUrlArgs from server-side GuildStatus.
 * Use in getServerSideProps where GuildStatus is available.
 */
export function getStreamingArgsFromGuildStatus(
  guildStatus: {
    guildPermissions?: Pick<GuildPermissions, 'streamIn4k' | 'streamIn1080p'>
  } | null,
): StreamingUrlArgs {
  if (!guildStatus) {
    return DEFAULT_STREAMING_ARGS
  }

  return getStreamingArgs({
    guildPermissions: guildStatus.guildPermissions,
  })
}

/** Default args for non-guild users or static pages (no user context) */
export const DEFAULT_STREAMING_ARGS: StreamingUrlArgs = {
  segmentFormat: 'TS',
  maxH264Level: 'LEVEL_4_1',
  maxHevcLevel: 'LEVEL_0',
  maxAudioChannels: 'CHANNELS_2',
}

/** Basic guild: up to 1080p, default 1080p */
const BASIC_STREAMING_ARGS: StreamingUrlArgs = {
  segmentFormat: 'TS',
  maxResolution: 'RESOLUTION_1080',
  defaultResolution: 'RESOLUTION_1080',
  maxH264Level: 'LEVEL_4_1',
  maxHevcLevel: 'LEVEL_0',
  maxAudioChannels: 'CHANNELS_2',
}

/**
 * Premium guild: up to 4K, default 1080p.
 *
 * NOTE: 4K resolution currently does not work because our backend does not serve 4K with TS segments.
 * 4K requires M4S segment format + HEVC codec. Other platforms (OTT) get 4K because they request
 * HEVC + M4S. Web is stuck on TS (and by extension AVC) due to a Bitmovin bug on Chrome that
 * causes subtitles to desync with M4S segments. We keep the 4K config here so it will work
 * automatically once we fix the Bitmovin player or switch to a different video player.
 */
const PREMIUM_STREAMING_ARGS: StreamingUrlArgs = {
  segmentFormat: 'TS',
  maxResolution: 'RESOLUTION_2160',
  defaultResolution: 'RESOLUTION_1080',
  maxH264Level: 'LEVEL_5_1',
  maxHevcLevel: 'LEVEL_0',
  maxAudioChannels: 'CHANNELS_2',
}
